import React, { useState } from 'react';
import { 
  Checkbox, 
  Button, 
  Space, 
  Dropdown, 
  Modal, 
  Form, 
  Select, 
  Input, 
  message, 
  Tag, 
  Typography,
  Divider
} from 'antd';
import { 
  DeleteOutlined, 
  TagOutlined, 
  FolderOpenOutlined, 
  MoreOutlined,
  CheckOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

const BatchOperations = ({
  items = [],
  selectedItems = [],
  onSelectionChange,
  onDelete,
  onTag,
  onMove,
  itemType = 'documents', // 'documents' or 'projects'
  projects = []
}) => {
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchAction, setBatchAction] = useState('');
  const [batchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(items.map(item => item.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, itemId]);
    } else {
      onSelectionChange(selectedItems.filter(id => id !== itemId));
    }
  };

  const getBatchActions = () => [
    {
      key: 'delete',
      label: 'Delete Selected',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleBatchAction('delete')
    },
    {
      key: 'tag',
      label: 'Add Tags',
      icon: <TagOutlined />,
      onClick: () => handleBatchAction('tag')
    },
    {
      key: 'move',
      label: 'Move to Project',
      icon: <FolderOpenOutlined />,
      onClick: () => handleBatchAction('move'),
      disabled: itemType === 'projects'
    }
  ];

  const handleBatchAction = (action) => {
    if (selectedItems.length === 0) {
      message.warning('Please select items first');
      return;
    }
    setBatchAction(action);
    setBatchModalVisible(true);
  };

  const handleBatchModalOk = async () => {
    try {
      setLoading(true);
      const values = await batchForm.validateFields();

      switch (batchAction) {
        case 'delete':
          await onDelete(selectedItems);
          message.success(`Deleted ${selectedItems.length} ${itemType}`);
          onSelectionChange([]);
          break;
        
        case 'tag':
          await onTag(selectedItems, values.tags);
          message.success(`Added tags to ${selectedItems.length} ${itemType}`);
          break;
        
        case 'move':
          await onMove(selectedItems, values.projectId);
          message.success(`Moved ${selectedItems.length} ${itemType} to project`);
          break;
      }

      setBatchModalVisible(false);
      batchForm.resetFields();
    } catch (error) {
      console.error('Batch operation failed:', error);
      message.error('Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const renderBatchModalContent = () => {
    switch (batchAction) {
      case 'delete':
        return (
          <div>
            <Text>Are you sure you want to delete {selectedItems.length} selected {itemType}?</Text>
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                This action cannot be undone. All selected items will be permanently removed.
              </Text>
            </div>
          </div>
        );
      
      case 'tag':
        return (
          <Form form={batchForm} layout="vertical">
            <Form.Item
              name="tags"
              label="Tags"
              rules={[{ required: true, message: 'Please select at least one tag' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select or create tags"
                style={{ width: '100%' }}
                options={[
                  { label: 'Blueprint', value: 'blueprint' },
                  { label: 'Permit', value: 'permit' },
                  { label: 'Contract', value: 'contract' },
                  { label: 'Invoice', value: 'invoice' },
                  { label: 'Report', value: 'report' },
                  { label: 'Specification', value: 'specification' },
                  { label: 'Drawing', value: 'drawing' },
                  { label: 'Photo', value: 'photo' }
                ]}
              />
            </Form.Item>
          </Form>
        );
      
      case 'move':
        return (
          <Form form={batchForm} layout="vertical">
            <Form.Item
              name="projectId"
              label="Target Project"
              rules={[{ required: true, message: 'Please select a project' }]}
            >
              <Select placeholder="Select project to move items to">
                {projects.map(project => (
                  <Option key={project.id} value={project.id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        );
      
      default:
        return null;
    }
  };

  const getBatchModalTitle = () => {
    switch (batchAction) {
      case 'delete':
        return `Delete ${selectedItems.length} ${itemType}`;
      case 'tag':
        return `Add Tags to ${selectedItems.length} ${itemType}`;
      case 'move':
        return `Move ${selectedItems.length} ${itemType}`;
      default:
        return 'Batch Operation';
    }
  };

  return (
    <div>
      {/* Batch Selection Controls */}
      <div style={{ 
        padding: '12px 16px', 
        background: '#f5f5f5', 
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Space>
          <Checkbox
            indeterminate={selectedItems.length > 0 && selectedItems.length < items.length}
            checked={selectedItems.length === items.length && items.length > 0}
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            Select All ({selectedItems.length}/{items.length})
          </Checkbox>
          
          {selectedItems.length > 0 && (
            <Tag color="blue" style={{ margin: 0 }}>
              {selectedItems.length} selected
            </Tag>
          )}
        </Space>

        <Dropdown
          menu={{
            items: getBatchActions(),
            onClick: ({ key }) => handleBatchAction(key)
          }}
          disabled={selectedItems.length === 0}
        >
          <Button 
            icon={<MoreOutlined />}
            disabled={selectedItems.length === 0}
          >
            Batch Actions
          </Button>
        </Dropdown>
      </div>

      {/* Batch Operations Modal */}
      <Modal
        title={getBatchModalTitle()}
        open={batchModalVisible}
        onOk={handleBatchModalOk}
        onCancel={() => {
          setBatchModalVisible(false);
          batchForm.resetFields();
        }}
        confirmLoading={loading}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{
          danger: batchAction === 'delete'
        }}
      >
        {renderBatchModalContent()}
      </Modal>
    </div>
  );
};

// Checkbox component for individual items
export const BatchCheckbox = ({ 
  itemId, 
  isSelected, 
  onSelect, 
  children, 
  style = {} 
}) => (
  <div 
    style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px',
      ...style 
    }}
  >
    <Checkbox
      checked={isSelected}
      onChange={(e) => onSelect(itemId, e.target.checked)}
    />
    {children}
  </div>
);

export default BatchOperations;
